import { FC, useEffect, useState } from "react";
import CommonLayout from "./CommonLayout";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentHolidayPackage, setHolidayPackage } from "application/features/holidayPackage/holidayPackageSlice";
import { useGetPropertiesByHostQuery } from "application/features/property/propertyApiSlice";
import { selectCurrentHost } from "application/features/host/hostSlice";
import StayCardH from "components/StayCardH/StayCardH";
import HolidayPackageComposer from "./HolidayPackageComposer";
import type { MemoContent, DroppedItem } from "./HolidayPackageComposer";
import Skeleton from "react-loading-skeleton";
import { useTranslation } from "react-i18next";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { Host } from "data/types/host";
import NotesModal from "./NotesModal";
import TimeModal from "./TimeModal";
import { useNavigate } from "react-router-dom";

export interface AddHolidayPackage6Props { }

const PageAddHolidayPackage6: FC<AddHolidayPackage6Props> = () => {

    const { t, ready } = useTranslation("translation");
    const holidayPackage = useSelector(selectCurrentHolidayPackage);
    const host: Host | null = useSelector(selectCurrentHost);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [showSkeleton, setShowSkeleton] = useState(true);
    const [draggedItem, setDraggedItem] = useState<any>(null);
    const [preparedEvents, setPreparedEvents] = useState<any[]>([]);

    const handleUpdateHolidayPackage = async (field: string, value: any) => {
        dispatch(setHolidayPackage({ ...holidayPackage, [field]: value }))
    };

    const handleAddNewProperty = () => {
        const returnTo = encodeURIComponent("/host/new-holiday-package/add-listing-6");
        navigate(`/host/new-property/add-listing-1?returnTo=${returnTo}`);
    };

    const { data = [], isLoading } = useGetPropertiesByHostQuery(host?.host_uuid, {
        skip: !host?.host_uuid,
        refetchOnMountOrArgChange: true,
    });

    useEffect(() => {
        if (!isLoading && data) {
            setShowSkeleton(false);
        }
      }, [isLoading]);


    const _updateDefaultFields = async () => {
        dispatch(setHolidayPackage({ 
            ...holidayPackage, 
            package_content: holidayPackage?.package_content? holidayPackage.package_content : []
        }))
    }

    function nextPageCondition() {
        return true
    }

    useEffect(() => {
        _updateDefaultFields();
        setPreparedEvents(prepareEvents(holidayPackage?.package_content ? holidayPackage.package_content : []));
        console.log("holidayPackage", holidayPackage);
    }, []);

    function definePreviousPage(package_type: string) {
        switch (package_type) {
            case "defined_guests":
                return "/host/new-holiday-package/add-listing-5a";
            default:
                return "/host/new-holiday-package/add-listing-5b";
        }
    }

    function onDragStart(event: any) {
        // At drag start, we can set the dragged item
        let element = event.target;
        while (element && !element.getAttribute("data-uuid")) {
            element = element.parentElement;
        }
        if (!element) {
            // console.log("onDragStart: element is null");
            return;
        }
        // console.log("onDragStart: element", element);
        setDraggedItem({
            uuid: element.getAttribute("data-uuid"),
            name: element.getAttribute("data-title"),
            type: "property",
        });
    }

    function onDrop(event: any): DroppedItem {
        const dayNumber = event.target.getAttribute("data-day-number");
        if (!draggedItem || !draggedItem.uuid || !draggedItem.name || !dayNumber ) {
            // console.log("onDrop: draggedItem or dayNumber is null");
            return {
                success: false,
                data: null,
            };
        }
        // Handle the drop event
        const oldPackageContent = holidayPackage?.package_content ? holidayPackage.package_content : [];
        const data = {title: draggedItem.name, uuid: draggedItem.uuid, type: draggedItem.type, day: parseInt(dayNumber), time: "", notes: ""} as MemoContent;
        let newPackageContent = JSON.parse(JSON.stringify(oldPackageContent));
        newPackageContent.push(data);
        handleUpdateHolidayPackage("package_content", newPackageContent);
        setDraggedItem(null);
        setPreparedEvents(prepareEvents(newPackageContent));
        return {
            success: true,
            data: data,
        }
    }

    function onDeleteItem(item: MemoContent) {
        const oldPackageContent = holidayPackage?.package_content ? holidayPackage.package_content : [];
        let newPackageContent = JSON.parse(JSON.stringify(oldPackageContent));
        // console.log("onDeleteItem: oldPackageContent", oldPackageContent);
        newPackageContent = newPackageContent.filter((content: MemoContent) => {
            return !(content.day === item.day && content.uuid === item.uuid);
        });
        // console.log("onDeleteItem: newPackageContent after filter", newPackageContent);
        handleUpdateHolidayPackage("package_content", newPackageContent);
        // console.log("onDeleteItem: newPackageContent", newPackageContent);
        setPreparedEvents(prepareEvents(newPackageContent));
    }

    function prepareEvents(package_content: any) {
        const days: number[] = Array.from({ length: holidayPackage?.duration_days || 1 }, (_, i) => i + 1);
        return days.map((dayIndex: number) => {
            const property = package_content.filter((item: MemoContent) => item.type === "property" && item.day === dayIndex);
            const experiences = package_content.filter((item: MemoContent) => item.type === "experience" && item.day === dayIndex);
            return (
                [...property, ...experiences]
            );
        });
    }

    const [ activeItem, setActiveItem ] = useState<MemoContent | null>(null);
    const [ showNotesModal, setShowNotesModal ] = useState(false);
    const [ modalNotes, setModalNotes ] = useState("");
    const [ showTimeModal, setShowTimeModal ] = useState(false);
    const [ modalTime, setModalTime ] = useState("");

    function onSetTime(item: MemoContent) {
        setModalTime(item.time || "");
        setActiveItem(item);
        setShowTimeModal(true);
    }

    function onSetNotes(item: MemoContent) {
        setModalNotes(item.notes || "");
        setActiveItem(item);
        setShowNotesModal(true);
    }

    const noteModalClose = (text: string) => {
        // console.log("note close: activeItem", activeItem, "note text", text);
        // Find the active item in the package content and update its notes
        if (activeItem) {
            const oldPackageContent = holidayPackage?.package_content ? holidayPackage.package_content : [];
            let newPackageContent = JSON.parse(JSON.stringify(oldPackageContent));
            newPackageContent = newPackageContent.map((content: MemoContent) => {
                if (content.uuid === activeItem.uuid && content.day === activeItem.day) {
                    return { ...content, notes: text };
                }
                return content;
            });
            handleUpdateHolidayPackage("package_content", newPackageContent);
            setPreparedEvents(prepareEvents(newPackageContent));
        } else {
            console.warn("noteModalClose: activeItem is null");
        }
        setModalNotes("");
        setShowNotesModal(false);
        setActiveItem(null);
    }

    const timeModalClose = (text: string) => {
        // console.log("time close: activeItem", activeItem, "time text", text); 
        // Find the active item in the package content and update its time
        if (activeItem) {
            const oldPackageContent = holidayPackage?.package_content ? holidayPackage.package_content : [];
            let newPackageContent = JSON.parse(JSON.stringify(oldPackageContent));
            newPackageContent = newPackageContent.map((content: MemoContent) => {
                if (content.uuid === activeItem.uuid && content.day === activeItem.day) {
                    return { ...content, time: text };
                }
                return content;
            });
            // console.log("time close: newPackageContent", newPackageContent); 
            handleUpdateHolidayPackage("package_content", newPackageContent);
            setPreparedEvents(prepareEvents(newPackageContent));
        } else {
            console.warn("timeModalClose: activeItem is null");
        }
        setModalTime("");
        setShowTimeModal(false);
        setActiveItem(null);
    }

    if (!ready) return null;

    return (
        <CommonLayout
            index="06"
            backtHref={definePreviousPage(holidayPackage?.package_type)}
            nextHref={"/host/new-holiday-package/add-listing-7"}
            canGoNext={nextPageCondition()}
            widePage={true}
            buttonPosition="up"
        >
        <>
            <div className="flex flex-col xl:flex-row w-full gap-4">
                <div className="w-full xl:w-5/12 2xl:w-1/3 max-h-[calc(60vh)] overflow-y-auto">
                    <div className="w-full">
                        <h2 className="text-xl sm:text-2xl font-semibold">{t("translation:components.holidayPackageForm.page6.title")}</h2>
                    </div>
                    <div className="flex flex-col px-2 md:px-4 py-2 md:py-4 gap-2">
                        {
                            isLoading || showSkeleton ?
                                Array.from(Array(3).keys()).map((item: any, index) => (
                                <div key={`skeleton-${index}`} className="w-full md:w-1/3 lg:w-full flex flex-row items-start justify-between p-2 md:p-4 gap-2 bg-white flex-wrap">
                                    <div className="md:basis-1/3 basis-full">
                                    <Skeleton className="w-full h-[15rem]" />
                                    </div>
                                    <div className="md:basis-2/3 basis-full flex flex-col">
                                    <Skeleton className="w-full h-[3rem]" />
                                    <Skeleton className="w-full h-[5rem] mt-3" />
                                    <div className="w-[10rem] self-end mt-3">
                                        <Skeleton className="w-full h-[3rem]" />
                                    </div>
                                    </div>

                                </div>
                                ))
                            :
                            data ?
                                data.map((item: any, index: number) => (    
                                    <StayCardH
                                        key={item.uuid || item.id || `property-${index}`}
                                        data={item}
                                        draggable={true}
                                        onDragStart={onDragStart}
                                        onDragEnd={() => {}}
                                    />
                                ))
                                :
                                (
                                    <div>
                                        No experiences available
                                    </div>
                                )
                        }

                        <div className="w-full flex flex-col items-center justify-between mt-5">
                            <h3 className="font-semibold mb-5">{t("translation:components.holidayPackageForm.page6.addNew")}</h3>
                            <div className="flex flex-row items-center gap-2">
                                <ButtonPrimary
                                    onClick={handleAddNewProperty}
                                >
                                    {t("translation:components.holidayPackageForm.page6.uploadButton")}
                                </ButtonPrimary>
                            </div>
                        </div>

                    </div>
                </div>
                <div id="calendar-view" className="w-full xl:w-7/12 2xl:w-2/3 max-h-[calc(60vh)] overflow-y-auto">
                    {/* Responsive two-column layout for calendar and events */}
                    <div className="flex flex-col lg:flex-row gap-4">
                        <div className="w-full lg:w-1/2 xl:w-2/5">
                            {/* Calendar Composer */}
                            <HolidayPackageComposer
                                title={t("translation:components.holidayPackageForm.page6.form.calendarTitle")}
                                description={t("translation:components.holidayPackageForm.page6.form.calendarDescription")}
                                days={holidayPackage?.duration_days ? holidayPackage.duration_days : 1}
                                onDrop={onDrop}
                                onDeleteItem={onDeleteItem}
                                onDragEnter={(event: any) => {}}
                                onDragOver={(event: any) => {}}
                                onDragLeave={(event: any) => {}}
                                onSetNotes={onSetNotes}
                                onSetTime={onSetTime}
                                events={preparedEvents}
                            />
                            <NotesModal
                                isOpen={showNotesModal}
                                notes={modalNotes}
                                closeModal={noteModalClose}
                            />
                            <TimeModal
                                isOpen={showTimeModal}
                                time={modalTime}
                                closeModal={timeModalClose}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
        </CommonLayout >
    );
};

export default PageAddHolidayPackage6;
